import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorTypes, FeatureFlags, Filter, QueryObjectTags } from '@fabric-ui/components';
import { FileFormat } from '@fabric-ui/components/Table';
import { noop } from '@fabric-ui/utils';
import { QueriesResults, QueryObserverOptions, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { patchHistoricalQueryTimeRange } from '@views/CustomDashboards/patchHistoricalQueryTimeRange';
import { QueryObject } from '@views/CustomDashboards/types';
import { gql } from 'graphql-request';
import { useAvContext } from '../context/AvContextProvider';
import { cleanEmptyFilters } from '../utils/filterUtils';
import { generateUUID } from '../utils/utils';
import { AliasResolution, FeatureFlagsOverrides } from '../views/CustomDashboards/types/QueryObject.types';
import { formatTags } from './queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import useDebounce from './useDebounce';
import useHandleError from './useHandleError';
import { CANCEL_ASYNC_QUERY, CANCEL_QUERY } from './useQuerySql';

const QUERY = gql`
  query queryObjectAsync($req: Json!) {
    queryObjectAsync(value: $req) {
      queryId
    }
  }
`;
const POLLING = gql`
  query asyncPollDwQuery($req: Json!) {
    asyncPollDwQuery(value: $req) {
      rows {
        data
        id
      }
      totalRowCount
      totals {
        data
        id
      }
      asyncResult
    }
  }
`;

export type QueryObjectProps = {
  queryObject: QueryObject | QueryObject[];
  origin?: string;
  showTotals?: boolean;
  totalRowCount?: boolean;
  onError?: (error) => void;
  options?: Partial<QueryObserverOptions>;
  muteErrors?: boolean;
  contextFilter?: Filter;
  debounceQueries?: boolean;
  searchQuery?: string;
  fileFormat?: string;
  aliasResolution?: AliasResolution;
  tags?: QueryObjectTags;
  featureFlagsOverrides?: FeatureFlagsOverrides;
};

export type UseQueryObjectProps = QueryObjectProps & { onSuccess?: (d: any[], index: number) => any[] };

export const useGetQueryObjectAsyncId = ({
  queryObject,
  origin = 'INTERACTIVE_QUERY',
  onError = undefined,
  showTotals = false,
  totalRowCount = false,
  onSuccess = d => d,
  options = {},
  muteErrors = undefined,
  contextFilter,
  debounceQueries = false,
  searchQuery,
  fileFormat = FileFormat.JSON_FORMAT,
  aliasResolution = [],
  tags,
  featureFlagsOverrides,
}: QueryObjectProps & { onSuccess?: (d: string) => string }): any => {
  const { api } = useAvContext();
  const { debouncedValue, isDebouncing } = useDebounce(
    {
      queriesObject: Array.isArray(queryObject) ? queryObject : [queryObject],
      options,
      showTotals,
      totalRowCount,
      contextFilter,
    },
    debounceQueries ? 3000 : 0
  );

  const cleanedContextFilter = useMemo(() => cleanEmptyFilters(debouncedValue.contextFilter), [debouncedValue.contextFilter]);
  const queries = debouncedValue.queriesObject.map((query, index) => ({
    queryKey:
      JSON.stringify(debouncedValue.queriesObject[index]) + JSON.stringify(cleanedContextFilter) + JSON.stringify(featureFlagsOverrides),
    queryFn: ({ signal }: { signal?: AbortSignal }) => {
      const queryId = generateUUID();
      return api(QUERY, {
        options: {
          req: {
            query,
            totals: { totalRow: debouncedValue.showTotals, totalRowCount: debouncedValue.totalRowCount },
            ...(origin && { origin }),
            ...(fileFormat && { fileFormat }),
            contextFilter: cleanedContextFilter,
            aliasResolution,
            searchQuery,
            tags: formatTags(tags),
            featureFlagsOverrides,
            queryId,
          },
        },
        onError,
        signal,
        muteErrors,
        onSuccess: data => {
          const flattenedData = data?.data?.queryObjectAsync?.queryId;
          if (data === undefined || data.errors) {
            throw data?.errors || new Error(`${ErrorTypes.Network}: ${debouncedValue.queriesObject[index]}`);
          }
          return onSuccess(flattenedData);
        },
      }).catch(e => {
        if (signal?.aborted) {
          api(CANCEL_QUERY, { options: { queryId }, muteErrors: true }).catch(noop);
          return;
        }
        if (muteErrors) {
          onError?.(e);
        } else {
          throw e;
        }
      });
    },
    ...debouncedValue.options,
    enabled:
      (debouncedValue.options.enabled || debouncedValue.options.enabled === undefined) &&
      (!!query?.select?.dims?.length || !!query?.select?.metrics?.length),
  }));
  // @ts-ignore
  const results: {
    data: any;
    refetch: () => Promise<any>;
    isLoading: boolean;
    isPending: boolean;
    isRefetching: boolean;
    isPreviousData: boolean;
    error?: any[];
  }[] = useQueries({ queries }) as QueriesResults<any>;
  const refetch = useCallback(() => Promise.all(results.map(r => r.refetch())), [results]);
  return useMemo(
    () => ({
      data: !results.length || results.every(({ data }) => !data) ? undefined : results.map(({ data }) => data),
      isLoading: results.some(result => result.isLoading),
      isPending: results.some(result => result.isPending),
      errors: results.map(result => result.error).filter(e => !!e) || [],
      refetch,
      isRefetching: results.some(result => result.isRefetching),
      isPreviousData: results.some(result => result.isPreviousData),
      isDebouncing,
    }),
    [results, isDebouncing]
  );
};

export const useAsyncPollDwQuery = ({
  queriesId,
  onSuccess = d => d,
  onError = undefined,
  options = {},
  muteErrors = undefined,
  isLoading,
  isRefetching,
}: {
  queriesId: string[];
  onSuccess?: (d: any[], index: number, asyncResult: string) => any[];
  onError?: (error) => void;
  options?: Partial<QueryObserverOptions>;
  muteErrors?: boolean;
  isLoading?: boolean;
  isRefetching?: boolean;
}) => {
  const { api } = useAvContext();
  const queryClient = useQueryClient();
  const { mutate: cancel } = useMutation({
    mutationFn: (queryId: string) => api(CANCEL_ASYNC_QUERY, { options: { queryId }, muteErrors: true }).catch(noop),
    onSettled: (data, error, variables) =>
      queryClient.resetQueries({
        predicate: ({ state: { data } }) => Boolean(data && typeof data === 'object' && 'queryId' in data && data.queryId === variables),
      }),
  });
  const queries = queriesId.map((queryId, index) => ({
    queryKey: [queryId],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(POLLING, {
        options: {
          req: {
            queryId,
          },
        },
        onError,
        signal,
        muteErrors,
        onSuccess: data => {
          const flattenedData = data?.data?.asyncPollDwQuery?.rows?.map(row => ({ ...row.data, id: row.id }));
          const asyncResult = data?.data?.asyncPollDwQuery?.asyncResult;
          if (data === undefined || data.errors) {
            throw data?.errors || new Error(`${ErrorTypes.Network}: ${queriesId[index]}`);
          }
          return {
            data: onSuccess(flattenedData, index, asyncResult),
            asyncResult,
            totals: data?.data?.asyncPollDwQuery?.totals?.data,
            totalRowCount: data?.data?.asyncPollDwQuery?.totalRowCount,
          };
        },
      }).catch(e => {
        if (!signal?.aborted) {
          if (muteErrors) {
            onError?.(e);
          } else {
            throw e;
          }
        } else {
          cancel(queryId);
        }
      }),
    ...options,
    enabled: (options.enabled || options.enabled === undefined) && !!queryId,
  }));

  // @ts-ignore
  const results: {
    data: { data?: any; asyncResult?: string; totals?: Record<string, number>; totalRowCount?: number };
    refetch: () => Promise<any>;
    isLoading: boolean;
    isPending: boolean;
    isRefetching: boolean;
    isPreviousData: boolean;
    error?: any[];
  }[] = useQueries({ queries });
  const refetch = useCallback(() => Promise.all(results.map(r => r.refetch())), [results]);
  return useMemo(
    () => ({
      asyncResult: !results.length
        ? undefined
        : results.length === 1
          ? results[0].data?.asyncResult
          : results.map(({ data }) => data?.asyncResult),
      totalRowCount: !results.length ? undefined : results[0].data?.totalRowCount,
      totals: !results.length ? undefined : results.length === 1 ? results[0].data?.totals : results.map(({ data }) => data?.totals),
      data:
        !results.length || (queries.length > 1 && results.some(({ data }) => !data?.data?.length))
          ? undefined
          : results.length === 1
            ? results[0].data?.data
            : results.map(({ data }) => data?.data),
      isLoading:
        results.some(result => result.isLoading) || isLoading || !!results.find(({ data }) => data?.asyncResult === 'ASYNC_RUNNING'),
      isPending: results.some(result => result.isPending) || !!results.find(({ data }) => data?.asyncResult === 'ASYNC_RUNNING'),
      errors: results.map(result => result.error).filter(e => !!e) || [],
      refetch,
      isRefetching:
        results.some(result => result.isRefetching) || isRefetching || !!results.find(({ data }) => data?.asyncResult === 'ASYNC_RUNNING'),
      isPreviousData: results.some(result => result.isPreviousData),
    }),
    [results]
  );
};

/**
 *
 *
 * @deprecated - use useQueryObjectAsyncWithCachedResult
 */
export const useQueryObjectAsync = ({
  queryObject,
  origin = 'INTERACTIVE_QUERY',
  onSuccess = d => d,
  onError = undefined,
  showTotals = false,
  totalRowCount = false,
  options = {},
  muteErrors = undefined,
  contextFilter,
  debounceQueries = false,
  searchQuery,
  fileFormat = 'JSON_FORMAT',
  aliasResolution,
  tags,
}: UseQueryObjectProps) => {
  const { featureFlags } = useAvContext();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);
  const [isPolling, setIsPolling] = useState(true);
  const [pollingIds, setPollingIds] = useState<string[]>([]);

  const patchedQueryObject = (Array.isArray(queryObject) ? queryObject : [queryObject]).map(q => ({
    ...q,
    timeRange: patchHistoricalQueryTimeRange(q.timeRange),
  }));

  const {
    data: newPollingIds,
    isLoading,
    isPending: isPendingId,
    isRefetching,
    isDebouncing,
    errors: queryIdErrors,
  } = useGetQueryObjectAsyncId({
    queryObject: patchedQueryObject,
    origin,
    showTotals,
    totalRowCount,
    onError,
    options,
    muteErrors,
    contextFilter,
    debounceQueries,
    searchQuery,
    fileFormat,
    aliasResolution,
    tags,
    onSuccess: d => {
      setPollingIds([...pollingIds, d]);
      setIsPolling(true);
      return d;
    },
  });

  useEffect(() => {
    if (pollingIds) {
      setIsPolling(true);
    }
  }, [pollingIds]);
  const result = useAsyncPollDwQuery({
    queriesId: newPollingIds?.every(id => !!id) ? newPollingIds : [undefined],
    isLoading,
    isRefetching,
    onSuccess: (d, i, asyncResult) => {
      setIsPolling(!(asyncResult === 'ASYNC_DONE'));
      return onSuccess(d, i);
    },
    onError: e => {
      setIsPolling(false);
      if (onError) {
        onError(e);
      } else {
        handleError(e);
      }
    },
    options: { ...options, refetchInterval: 3000, refetchIntervalInBackground: true, enabled: !!pollingIds.length && isPolling },
  });

  return {
    ...result,
    isPending: isPendingId || result.isPending,
    errors: [...(queryIdErrors ?? []), ...(result.errors ?? [])],
    isDebouncing: Boolean(isDebouncing),
  };
};
